import { checkSession } from "@features/auth";
import { getUserId } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shadcn/ui/tabs";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Boxes, ChevronRight, Coins, PackagePlus, Repeat, Settings } from "lucide-react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import z from "zod";
import { AppHeader } from "~/components/layout/AppHeader";
import { BackLink } from "~/components/one-delivery/BackLink";
import { PageContainer } from "~/components/layout/PageContainer";
import { ACTIVE_STATUSES, bundleOrdinal, tabOf, type ShipmentTab } from "~/components/one-delivery/bundle-status";
import { requireOnboarded } from "~/components/one-delivery/onboarding";
import { PackageTrack } from "~/components/one-delivery/PackageTrack";
import type { oneDeliveryShipmentsQuery, BundleStatus } from "./__generated__/oneDeliveryShipmentsQuery.graphql";

const query = graphql`
	query oneDeliveryShipmentsQuery($userId: UUID!, $statuses: [BundleStatus!]!) {
		creditBalance {
			available
		}
		creditSubscription {
			id
			status
			cancelAtPeriodEnd
			currentPeriodEnd
			plan {
				id
				title
				credits
			}
		}
		bundles(filter: { userId: $userId, statuses: $statuses }, pagination: { page: 0, pageSize: 100 }) {
			nodes {
				id
				status
				createdAt
				warehouse {
					id
					name
				}
				hub {
					id
					meta {
						id
						name
					}
				}
				packages(pagination: { page: 0, pageSize: 100 }) {
					totalCount
				}
			}
		}
	}
`;

export const Route = createFileRoute("/one-delivery/")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery";
	},
	component: RouteComponent,
	validateSearch: zodValidator(z.object({ tab: z.enum(["bundle", "delivery", "pickup"]).optional() })),
	loader: async ({ context }) => {
		const userId = (await checkSession()) ? getUserId() : undefined;
		if (!userId) throw redirect({ to: "/auth", search: { redirectToPath: "/one-delivery", show: "sign-in" } });
		await requireOnboarded(context.relayEnvironment);
		return preloadQuery<oneDeliveryShipmentsQuery>(context.relayEnvironment, query, {
			userId,
			statuses: ACTIVE_STATUSES as unknown as BundleStatus[],
		});
	},
});

type BundleNode = oneDeliveryShipmentsQuery["response"]["bundles"]["nodes"][number];

function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const { locale } = useI18nContext();
	const { bundles, creditBalance, creditSubscription } = usePreloadedQuery<oneDeliveryShipmentsQuery>(query, Route.useLoaderData());
	// Oldest first so "Bundle 1" stays stable while new bundles are added.
	const sorted = [...bundles.nodes].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
	const byTab = (tab: ShipmentTab) => sorted.filter((bundle) => tabOf(bundle.status) === tab);
	const lists = { bundle: byTab("bundle"), delivery: byTab("delivery"), pickup: byTab("pickup") };
	const { tab = lists.pickup.length ? "pickup" : lists.delivery.length ? "delivery" : "bundle" } = Route.useSearch();

	const empty = { bundle: i18n.OD_EMPTY_BUNDLE(), delivery: i18n.OD_EMPTY_DELIVERY(), pickup: i18n.OD_EMPTY_PICKUP() };

	return (
		<PageContainer className="bg-slate-50">
			<AppHeader />
			<BackLink to="/" />
			<div className="flex flex-col gap-2 px-4 pt-4 pb-8">
				<h1 className="text-2xl font-breit font-normal text-neutral-900">
					<T>{i18n.OD_MY_SHIPMENTS()}</T>
				</h1>

				{/* How to ship a new order: always reachable, whatever tab is open. */}
				<Link to="/one-delivery/new-shipment" className={newShipmentCard}>
					<NewShipmentLabel />
				</Link>

				<Link
					to="/one-delivery/credits"
					className="flex flex-col divide-y divide-neutral-200 rounded-[14px] border border-neutral-200 bg-white no-underline shadow-sm"
				>
					<span className="flex items-center justify-between gap-3 px-5 py-3">
						<span className="flex items-center gap-3">
							<Coins className="size-5 text-neutral-950" />
							<span className="flex flex-col">
								<span className="text-xs text-muted-foreground">{i18n.OD_CREDITS_BALANCE()}</span>
								<span className="text-lg font-medium tabular-nums text-neutral-950">{creditBalance.available}</span>
							</span>
						</span>
						<span className="font-breit text-sm text-neutral-950">{i18n.OD_CREDITS_BUY()}</span>
					</span>
					{creditSubscription && (
						<span className="flex items-center gap-3 px-5 py-3">
							<Repeat className="size-5 shrink-0 text-neutral-950" />
							<span className="flex min-w-0 flex-1 flex-col">
								<span className="truncate text-sm font-medium text-neutral-950">
									{creditSubscription.plan.title} · {i18n.OD_SUBS_COUNT({ count: creditSubscription.plan.credits })}
								</span>
								{["INCOMPLETE", "PAST_DUE", "UNPAID"].includes(creditSubscription.status) ? (
									<span className="text-xs text-amber-700">{i18n.OD_SUBS_STATUS_PAYMENT_DUE()}</span>
								) : (
									creditSubscription.currentPeriodEnd && (
										<span className="text-xs text-muted-foreground">
											{(creditSubscription.cancelAtPeriodEnd ? i18n.OD_SUBS_ENDS : i18n.OD_SUBS_RENEWS)({
												date: new Date(creditSubscription.currentPeriodEnd).toLocaleDateString(locale, {
													day: "numeric",
													month: "long",
													year: "numeric",
												}),
											})}
										</span>
									)
								)}
							</span>
							<ChevronRight className="size-4 shrink-0 text-neutral-950" />
						</span>
					)}
				</Link>

				<Link to="/one-delivery/settings" className={newShipmentCard}>
					<span className="flex items-center gap-3">
						<Settings className="size-5 text-neutral-950" />
						<span className="font-breit text-sm text-neutral-950">{i18n.OD_SETTINGS()}</span>
					</span>
					<ChevronRight className="size-4 text-neutral-950" />
				</Link>

				<Tabs value={tab} onValueChange={(value) => navigate({ to: "/one-delivery", search: { tab: value as ShipmentTab } })}>
					<div className="flex justify-center py-4">
						<TabsList className="h-9 rounded-[10px] bg-neutral-100 p-[3px]">
							<ShipmentTabTrigger value="bundle" label={i18n.OD_TAB_BUNDLE()} count={lists.bundle.length} />
							<ShipmentTabTrigger value="delivery" label={i18n.OD_TAB_IN_DELIVERY()} count={lists.delivery.length} />
							<ShipmentTabTrigger value="pickup" label={i18n.OD_TAB_PICKUP()} count={lists.pickup.length} />
						</TabsList>
					</div>
					{(["bundle", "delivery", "pickup"] as const).map((key) => (
						<TabsContent key={key} value={key}>
							<div className="flex flex-col gap-4">
								{lists[key].map((bundle) => (
									<ShipmentCard key={bundle.id} bundle={bundle} index={bundleOrdinal(bundles.nodes, bundle.id) ?? 0} />
								))}
								{!lists[key].length && <p className="text-center text-sm text-muted-foreground">{empty[key]}</p>}
							</div>
						</TabsContent>
					))}
				</Tabs>
			</div>
		</PageContainer>
	);
}

const newShipmentCard =
	"flex w-full items-center justify-between gap-3 rounded-[14px] border border-neutral-200 bg-white px-5 py-3 text-left no-underline shadow-sm";

function NewShipmentLabel() {
	const i18n = useI18nContext().LL["new-ui"];
	return (
		<>
			<span className="flex items-center gap-3">
				<PackagePlus className="size-5 shrink-0 text-neutral-950" />
				<span className="flex flex-col">
					<span className="font-breit text-sm font-medium text-neutral-950">{i18n.OD_NEW_SHIPMENT()}</span>
					<span className="text-xs text-muted-foreground">{i18n.OD_NEW_SHIPMENT_TEXT()}</span>
				</span>
			</span>
			<ChevronRight className="size-4 shrink-0 text-neutral-950" />
		</>
	);
}

/** Segmented tab with a count badge: pink when the tab is active, dark otherwise (Figma "OD - Shipments"). */
function ShipmentTabTrigger({ value, label, count }: { value: string; label: string; count: number }) {
	return (
		<TabsTrigger value={value} className="group h-full gap-2 rounded-lg px-2 py-1 font-breit text-sm font-medium text-neutral-950">
			{label}
			{count > 0 && (
				<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-neutral-900 px-1 font-sans text-xs font-medium text-neutral-50 group-data-[state=active]:bg-accent-foreground">
					{count}
				</span>
			)}
		</TabsTrigger>
	);
}

function ShipmentCard({ bundle, index }: { bundle: BundleNode; index: number }) {
	const i18n = useI18nContext().LL["new-ui"];
	return (
		<Link to="/one-delivery/bundle/$bundleId" params={{ bundleId: bundle.id }} className="block no-underline">
			<div className="overflow-hidden rounded-3xl border border-neutral-200 bg-white">
				<div className="flex flex-col gap-2 bg-black p-5 text-white">
					<div className="flex items-center justify-between gap-3">
						<p className="font-breit text-3xl font-normal">{i18n.OD_BUNDLE_TITLE({ index })}</p>
						<span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-950 shadow-xs">
							<Boxes className="size-4" />
						</span>
					</div>
					<div className="flex items-center justify-between gap-3 text-base">
						<span>{i18n.OD_PICKUP_LOCATION()}</span>
						<span className="min-w-0 truncate font-medium">{bundle.hub?.meta?.name ?? i18n.OD_NO_DESTINATION()}</span>
					</div>
				</div>
				<div className="px-5 pt-4 pb-5">
					<PackageTrack count={bundle.packages.totalCount} showLegend />
				</div>
			</div>
		</Link>
	);
}
